import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { ColorPalette, EndClientReportTheme } from '../../../models/end-client-reporting-package';

/** The select's value for OR Default, which has no id. Never a real theme id. */
const DEFAULT_VALUE = '__default__';

/**
 * NK-450 — choosing a theme: a select of OR Default and the client's themes, the chosen one's five
 * colours beside it, and an "Update available" link when the theme has moved on since its colours
 * were applied.
 *
 * In core because three places use it: the create-report modal and template modal in `main`, and
 * the deck editor here. It only reports a choice -- applying the colours, and saving, belong to
 * whoever holds the design.
 */
@Component({
  selector: 'or-theme-picker',
  standalone: false,
  templateUrl: './theme-picker.component.html',
  styleUrls: ['./theme-picker.component.scss'],
})
export class ThemePickerComponent {
  /** OR Default and the client's themes, as the API lists them. */
  @Input({ required: true }) set themes(value: EndClientReportTheme[]) {
    this.themeList.set(value ?? []);
  }

  /** The chosen theme; null is OR Default. */
  @Input() set themeId(value: string | null | undefined) {
    this.selectedId.set(value ?? null);
  }

  /**
   * Shown when nothing in the list matches the id: a theme since deleted, which a revert can bring
   * back. Said plainly rather than shown as OR Default, which its colours aren't.
   */
  @Input() deletedLabel = 'Deleted theme';

  /** The theme has been edited since its colours were applied here. */
  @Input() outOfDate = false;
  @Input() disabled = false;
  @Input() inputId = 'theme-picker';
  @Input() label = 'Theme';
  /** Compact: no visible label, for the deck editor's top bar. */
  @Input() inline = false;
  /** So the label can match the form it sits in -- the modals bold theirs. */
  @Input() labelClass = 'nav-font12 grey5 mb-1';
  /** The chosen theme's colours beside it. Off where the colours are already shown right there. */
  @Input() showSwatches = true;
  /** Take the width it is given, shrinking below the usual minimum -- for a narrow column. */
  @Input() fill = false;

  /** The theme someone picked, OR Default included. */
  @Output() picked = new EventEmitter<EndClientReportTheme>();
  /** Asked to re-apply the current theme's latest colours. */
  @Output() updateRequested = new EventEmitter<void>();

  private themeList = signal<EndClientReportTheme[]>([]);
  private selectedId = signal<string | null>(null);

  readonly defaultValue = DEFAULT_VALUE;

  options = computed(() => this.themeList().map(theme => ({
    value: theme.id ?? DEFAULT_VALUE,
    text: theme.name,
  })));

  selected = computed(() => this.themeList().find(theme => theme.id === this.selectedId()) ?? null);

  /** A themeId with no theme behind it, once the list is loaded. */
  missing = computed(() => !!this.selectedId() && this.themeList().length > 0 && !this.selected());

  selectValue = computed(() => this.selectedId() ?? DEFAULT_VALUE);

  readonly roles: (keyof ColorPalette)[] = ['primary', 'secondary', 'accent', 'positive', 'negative'];

  choose(value: string) {
    const theme = this.themeList().find(candidate => (candidate.id ?? DEFAULT_VALUE) === value);
    if (!theme) return;
    this.selectedId.set(theme.id);
    this.picked.emit(theme);
  }
}
